import { useMutation, useQuery } from "@apollo/client";
import {
  ActionIcon,
  Button,
  Grid,
  LoadingOverlay,
  MultiSelect,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { CREATE_PRODUCT } from "apollo/mutuations";
import {
  GET_ALL_SUPPLIERS,
  GET_PRODUCTS,
  NON_PAGINATED_CATEGORIES,
} from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { useEffect, useRef, useState } from "react";
import { Trash } from "tabler-icons-react";
import { MAX_FILE_SIZE } from "utiles/url";

const ProductAddModal = ({
  setOpened,
  total,
  setTotal,
  activePage,
  setActivePage,
  refetchProducts,
  category_id,
}) => {
  const theme = useMantineTheme();
  const [suppliers, setSuppliers] = useState([]);

  const [business, setBusiness] = useState([]);
  const {
    data,
    supplierLoading,
    refetch: supplierRefetch,
  } = useQuery(GET_ALL_SUPPLIERS, {
    variables: {
      first: 100000,
      page: activePage,
      search: "",
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
    onCompleted(data) {
      const arr = data.getSuppliers.data.map((item) => ({
        label: item.user.name,
        value: item.id,
      }));

      setSuppliers(arr);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Suppliers",
      });
    },
  });

  const [addProduct, { loading, error }] = useMutation(CREATE_PRODUCT, {
    update(cache, { data: { createProduct } }) {
      cache.updateQuery(
        {
          query: GET_PRODUCTS,
          variables: {
            category_id: category_id,
            first: parseInt(10),
            page: activePage,
            ordered_by: [
              {
                column: "CREATED_AT",
                order: "DESC",
              },
            ],
            search: "",
          },
        },
        (data) => {
          if (data) {
            return {
              products: {
                ...data.products,
                data: [createProduct, ...data.products.data],
              },
            };
          }
        },
      );
    },
    onError(err) {
      showNotification({
        color: "red",
        title: "Error",
        message: "Failed to create product. Please try again.",
      });
    },
  });

  const { height } = useViewportSize();
  const [dropDownData, setDropDownData] = useState([]);
  const [attributes, setAttributeValues] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState({});

  const [subcategories, setSubcategories] = useState([]);
  const removeFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };
  // graphql queries
  const { loading: categoryLoading, refetch } = useQuery(
    NON_PAGINATED_CATEGORIES,
    {
      onCompleted(data) {
        const enArr = data.categoryNonPaginated.map((item) => ({
          label: item.name,
          value: item.id,
          children: item.children,
          attributes: item.attributes,
        }));
        setDropDownData(enArr);
      },
    },
  );
  useEffect(() => {
    refetch();
    supplierRefetch();
  }, []);

  const form = useForm({
    initialValues: {
      attributes: {
        create: [],
      },
      name: "",
      short_description: "",
      description: "",
      category: { connect: 10 },
      subcategory: { connect: "" },
      images: [],
      sell_price: "",
      buy_price: "",
      minimum_order_quantity: 0,
      supplier_id: 2,
      supplier_user_id: 1,

      is_active: true,
      product_type: "SIMPLE_PRODUCT",
    },
    validate: {
      sell_price: (value) => {
        if (value === "" || value === null || value === undefined)
          return "Sell price is required";
        if (isNaN(Number(value))) return "Must be a number";
        if (Number(value) < 0) return "Price can't be negative";
        if (Number(value) === 0) return "Price can't be zero";
        return null;
      },
      buy_price: (value) => {
        if (value === "" || value === null || value === undefined)
          return "Buy price is required";
        if (isNaN(Number(value))) return "Must be a number";
        if (Number(value) < 0) return "Price can't be negative";
        if (Number(value) === 0) return "Price can't be zero";
        return null;
      },
      minimum_order_quantity: (value) => {
        if (value === "" || value === null || value === undefined) return null;
        if (isNaN(Number(value))) return "Must be a number";
        if (Number(value) < 0) return "Quantity can't be negative";
        if (Number(value) === 0) return "Quantity can't be zero";
        return null;
      },
    },
  });
  const handleSupplierChange = (val) => {
    form.setFieldValue("supplier_user_id", val);
    const selectedSupplier = data?.getSuppliers.data.find(
      (cat) => cat.id === val,
    );
    setBusiness(selectedSupplier ? selectedSupplier.businesses : []);
    form.setFieldValue("supplier_id", "");
  };
  const handleSupplierBusinessChange = (val) => {
    form.setFieldValue("supplier_id", val);
  };

  const handleAttributeChange = (attributeId, selectedValueIds) => {
    setSelectedAttributes((prev) => ({
      ...prev,
      [attributeId]: selectedValueIds,
    }));

    const updatedCreate = Object.entries({
      ...selectedAttributes,
      [attributeId]: selectedValueIds,
    })
      .filter(([_, valueIds]) => valueIds.length > 0)
      .map(([id, valueIds]) => ({
        attribute_id: id,
        attribute_value_ids: valueIds,
      }));

    form.setFieldValue("attributes.create", updatedCreate);
  };

  const handleCategoryChange = (val) => {
    form.setFieldValue("category.connect", val);
    const selectedCategory = dropDownData.find((cat) => cat.value === val);
    setAttributeValues(selectedCategory.attributes);
    setSubcategories(selectedCategory ? selectedCategory.children : []);
    form.setFieldValue("subcategory.connect", "");
  };

  const submit = () => {
    if (
      form.getInputProps("product_type").value === "VARIABLE_PRODUCT" &&
      form.getInputProps("attributes.create").value.length === 0
    ) {
      showNotification({
        color: "red",
        title: "Error",
        message: "Please add at least one attribute before submitting.",
      });
      return;
    }
    if (files.length === 0) {
      showNotification({
        color: "red",
        title: "Error",
        message: "Please add at least one image before submitting.",
      });
      return;
    }

    let variables = {
      name: form.getInputProps("name").value,
      short_description: form.getInputProps("short_description").value,
      description: form.getInputProps("description").value,
      is_active: form.getInputProps("is_active").value,
      buy_price: parseFloat(form.values.buy_price),
      sell_price: parseFloat(form.values.sell_price),
      attributes: form.getInputProps("attributes").value,
      product_type: form.getInputProps("product_type").value,
      minimum_order_quantity: parseInt(
        form.getInputProps("minimum_order_quantity").value,
      ),
      images: [...files],
      category: form.getInputProps("subcategory").value,
      supplier_business_id: form.getInputProps("supplier_id").value,
    };

    addProduct({
      variables,
      onCompleted() {
        refetchProducts();
        showNotification({
          color: "green",
          title: "Success",
          message: "Product Created Successfully",
        });
        setOpened(false);
      },
      onError(error) {
        setOpened(true);
        const errorMessage =
          error?.graphQLErrors?.[0]?.message ||
          "Product Not Created Successfully";
        showNotification({
          color: "red",
          title: "Error",
          message: errorMessage,
        });
      },
    });
  };

  const [files, setFiles] = useState([]);
  const previews = files.map((file, index) => {
    const imageUrl = URL.createObjectURL(file);
    return (
      <div
        key={index}
        style={{
          position: "relative",
          width: "130px",
          height: "130px",
          marginRight: "4px",
          marginBottom: "4px",
        }}
      >
        <img
          src={imageUrl}
          alt={`Preview ${index}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "8px",
          }}
          onLoad={() => URL.revokeObjectURL(imageUrl)}
        />
        <ActionIcon
          color="red"
          style={{
            position: "absolute",
            top: "5px",
            right: "5px",
            background: "white",
            borderRadius: "50%",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
          }}
          onClick={() => removeFile(index)}
        >
          <Trash size={20} />
        </ActionIcon>
      </div>
    );
  });

  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = [];
    let hasError = false;

    selectedFiles.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        showNotification({
          color: "red",
          title: "Error",
          message: `${file.name} exceeds 1 MB. Please upload a smaller file.`,
        });
        hasError = true;
      } else {
        validFiles.push(file);
      }
    });

    if (!hasError) {
      setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    }
  };

  return (
    <>
      <LoadingOverlay
        visible={loading || categoryLoading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <ScrollArea
        style={{ height: height / 1.8, marginLeft: "20px" }}
        type="auto"
        offsetScrollbars
      >
        <form onSubmit={form.onSubmit(() => submit())}>
          <Grid align="flex-start" gutter={8} style={{ flexWrap: "nowrap" }}>
            <Grid.Col span={6}>
              <Stack>
                <Grid>
                  <Grid.Col span={6}>
                    <TextInput
                      required
                      label="Product Name"
                      placeholder="Enter product name"
                      {...form.getInputProps("name")}
                    />
                  </Grid.Col>
                </Grid>
                <Grid>
                  <Grid.Col span={6}>
                    <Select
                      searchable
                      required
                      data={suppliers}
                      value={form
                        .getInputProps("supplier_user_id")
                        .value.toString()}
                      onChange={handleSupplierChange}
                      label="Supplier"
                      placeholder="Pick a supplier this product belongs to"
                    />
                  </Grid.Col>

                  <Grid.Col span={6}>
                    <Select
                      searchable
                      required
                      data={business.map((sub) => ({
                        label: sub.business_name,
                        value: sub.id,
                      }))}
                      value={form.getInputProps("supplier_id").value.toString()}
                      onChange={handleSupplierBusinessChange}
                      label="Supplier Business"
                      placeholder="Pick a business this product belongs to"
                    />
                  </Grid.Col>
                </Grid>
                <Grid>
                  <Grid.Col span={6}>
                    <Select
                      searchable
                      data={dropDownData}
                      value={form
                        .getInputProps("category.connect")
                        .value.toString()}
                      onChange={handleCategoryChange}
                      label="Category"
                      placeholder="Pick a category this product belongs to"
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Select
                      searchable
                      data={subcategories.map((sub) => ({
                        label: sub.name,
                        value: sub.id,
                      }))}
                      value={form
                        .getInputProps("subcategory.connect")
                        .value.toString()}
                      onChange={(val) =>
                        form.setFieldValue("subcategory.connect", val)
                      }
                      label="Subcategory"
                      placeholder="Pick a subcategory this product belongs to"
                      disabled={subcategories.length === 0}
                    />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={6}>
                    <Select
                      searchable
                      required
                      data={[
                        { value: "SIMPLE_PRODUCT", label: "Simple Product" },
                        {
                          value: "VARIABLE_PRODUCT",
                          label: "Variable Product",
                        },
                      ]}
                      label="Product Type"
                      placeholder="Pick a Type this products belongs to"
                      {...form.getInputProps("product_type")}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput
                      type="number"
                      label="Minimum Order"
                      placeholder="Minimum Order"
                      {...form.getInputProps("minimum_order_quantity")}
                    />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={6}>
                    <Textarea
                      placeholder="Short Description"
                      label="Short Description"
                      required
                      {...form.getInputProps("short_description")}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Textarea
                      placeholder="Description"
                      label="Description"
                      required
                      {...form.getInputProps("description")}
                    />
                  </Grid.Col>
                </Grid>

                {form.getInputProps("product_type").value ===
                  "SIMPLE_PRODUCT" && (
                  <Grid>
                    <Grid.Col span={6}>
                      <TextInput
                        placeholder="Sell Price"
                        label="Sell Price"
                        type="number"
                        step="any"
                        {...form.getInputProps("sell_price")}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput
                        placeholder="Buy price"
                        label="Buy price"
                        type="number"
                        step="any"
                        {...form.getInputProps("buy_price")}
                      />
                    </Grid.Col>
                  </Grid>
                )}
              </Stack>
            </Grid.Col>
            <Grid.Col span={4} style={{ marginLeft: "30px" }}>
              <ScrollArea
                style={{ height: height / 2 }}
                type="auto"
                offsetScrollbars
              >
                {form.getInputProps("product_type").value ===
                  "VARIABLE_PRODUCT" &&
                  attributes.length > 0 && (
                    <>
                      <Text weight={600} size="md" mb="sm">
                        Attributes and Values
                      </Text>
                      <Grid>
                        {attributes.map((attribute) => (
                          <Grid.Col span={8} key={attribute.id}>
                            <MultiSelect
                              label={attribute.name}
                              placeholder={`Select ${attribute.name}`}
                              data={attribute.values.map((val) => ({
                                value: val.id,
                                label: val.value,
                              }))}
                              value={selectedAttributes[attribute.id] || []}
                              onChange={(val) =>
                                handleAttributeChange(attribute.id, val)
                              }
                              searchable
                            />
                          </Grid.Col>
                        ))}
                      </Grid>
                    </>
                  )}
              </ScrollArea>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={12}>
              <Button
                onClick={() => fileInputRef.current.click()}
                variant="outline"
                color="blue"
                style={{
                  width: "24%",
                }}
              >
                Upload Images
              </Button>
              <input
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                multiple
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <SimpleGrid
                cols={8}
                spacing={2}
                breakpoints={[{ maxWidth: "sm", cols: 2 }]}
                mt={files.length > 0 ? "xl" : 0}
              >
                {previews}
              </SimpleGrid>
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={6}>
              <Button
                style={{
                  width: "30%",
                  marginTop: "15px",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
                type="submit"
              >
                Submit
              </Button>
            </Grid.Col>
          </Grid>
        </form>
      </ScrollArea>
    </>
  );
};

export default ProductAddModal;
